import { httpInterceptor } from '../http-interceptor';
import { normalizePlannerGetByIdPathSegment } from './planner-getbyid-path';
import { sanitizeSavePlanPayload } from './planner-save-plan-body';

/** Heavy report query (wide date range, all customers); default 30s API timeout is too low. */
const CUSTOMER_DEMAND_DETAIL_TIMEOUT_MS = 120000;

/** Shipment upsert from planner load board can include many lines; allow same extended window as demand detail. */
const UPSERT_SHIPMENTS_TIMEOUT_MS = 120000;

export class PlannerService {
  static ENDPOINTS = {
    GET_ALL_CHANNEL: 'planner/getallchannel',
    GET_ALL_TASK: 'planner/getalltask',
    SAVE_PLAN: 'planner/saveplan',
    UPDATE_PROCESS: 'planner/updateprocess',
    GET_BY_WORKTICKET_ID: 'planner/getbyid/',
    DELETE_BY_WORKTICKET_ID: 'planner/delete/',
    GET_STATUS_LOG: 'planner/getstatuslog/',
    GET_CUSTOMER_DEMAND_DETAIL: 'planner/getCustomerDemandDetail',
    GET_CUSTOMER_DEMAND_DETAIL_SHIPPING_PLANNER: 'planner/getCustomerDemandDetailShippingPlaner',
    GET_MATERIAL_PLANNER_COMPONENT_TYPES: 'planner/getMaterialPlannerComponentTypes',
    GET_MATERIAL_PLANNER_WORKSPACE: 'planner/getMaterialPlannerWorkspace',
    UPSERT_SHIPMENTS_AND_ITEMS: 'planner/upsertShipmentsAndItems',
  };

  static async getAllChannel(data) {
    return httpInterceptor.get(this.ENDPOINTS.GET_ALL_CHANNEL);
  }

  static async getAllTask(
    data = {
      // workTicketNumber: 'string',
      // workTicketName: 'string',
      // plannedStartDate: '2025-02-15T00:05:48.799Z',
      // plannedEndDate: '2025-02-15T00:05:48.799Z',
    },
  ) {
    return httpInterceptor.post(this.ENDPOINTS.GET_ALL_TASK, data);
  }

  static async savePlan(data) {
    return httpInterceptor.post(this.ENDPOINTS.SAVE_PLAN, sanitizeSavePlanPayload(data));
  }

  static async updateProcess(data) {
    return httpInterceptor.post(this.ENDPOINTS.UPDATE_PROCESS, sanitizeSavePlanPayload(data));
  }

  static async getByWorkTicketId(id) {
    const pathId = normalizePlannerGetByIdPathSegment(id);
    return httpInterceptor.get(`${this.ENDPOINTS.GET_BY_WORKTICKET_ID}${pathId}`);
  }

  static async deleteByWorkTicketId(id) {
    return httpInterceptor.delete(`${this.ENDPOINTS.DELETE_BY_WORKTICKET_ID}${id}`);
  }

  static async getWorkTicketStatusLog(id) {
    return httpInterceptor.get(`${this.ENDPOINTS.GET_STATUS_LOG}${id}`);
  }

  /**
   * @param {{ customer?: string; dueDateFrom?: string; dueDateTo?: string }} filters
   * @returns {Promise<{ response?: unknown[]; Response?: unknown[] }>}
   */
  static async getCustomerDemandDetail(filters = {}) {
    const params = {};
    if (filters.customer != null && String(filters.customer).trim() !== '') {
      params.customer = String(filters.customer).trim();
    }
    if (filters.dueDateFrom) params.dueDateFrom = filters.dueDateFrom;
    if (filters.dueDateTo) params.dueDateTo = filters.dueDateTo;
    return httpInterceptor.get(this.ENDPOINTS.GET_CUSTOMER_DEMAND_DETAIL, params, {
      timeoutMs: CUSTOMER_DEMAND_DETAIL_TIMEOUT_MS,
    });
  }

  /**
   * @param {{ customer?: string; dueDateFrom?: string; dueDateTo?: string }} filters
   * @returns {Promise<{ response?: unknown[]; Response?: unknown[] }>}
   */
  static async getCustomerDemandDetailShippingPlaner(filters = {}) {
    const params = {};
    if (filters.customer != null && String(filters.customer).trim() !== '') {
      params.customer = String(filters.customer).trim();
    }
    if (filters.dueDateFrom) params.dueDateFrom = filters.dueDateFrom;
    if (filters.dueDateTo) params.dueDateTo = filters.dueDateTo;
    return httpInterceptor.get(this.ENDPOINTS.GET_CUSTOMER_DEMAND_DETAIL_SHIPPING_PLANNER, params, {
      timeoutMs: CUSTOMER_DEMAND_DETAIL_TIMEOUT_MS,
    });
  }

  /**
   * @returns {Promise<{ response?: Array<string | { componentType?: string; ComponentType?: string }>; Response?: Array<string | { componentType?: string; ComponentType?: string }> }>}
   */
  static async getMaterialPlannerComponentTypes() {
    return httpInterceptor.get(this.ENDPOINTS.GET_MATERIAL_PLANNER_COMPONENT_TYPES);
  }

  /**
   * @param {string} materialType
   * @returns {Promise<{ response?: unknown[]; Response?: unknown[] }>}
   */
  static async getMaterialPlannerWorkspace(materialType) {
    const type = String(materialType || '').trim() || 'MATERIAL';
    return httpInterceptor.get(`${this.ENDPOINTS.GET_MATERIAL_PLANNER_WORKSPACE}/${encodeURIComponent(type)}`, undefined, {
      timeoutMs: CUSTOMER_DEMAND_DETAIL_TIMEOUT_MS,
    });
  }

  /**
   * Persists shipping planner load board as shipment header + line items (SQL OPENJSON).
   * @param {{
   *   shippingJson: string;
   *   shippingItemsJson: string;
   *   updateFullHeader?: boolean;
   *   softDeleteMissingItems?: boolean;
   *   requestedAspNum?: string;
   *   sourceFileName?: string;
   * }} body
   */
  static async upsertShipmentsAndItems(body) {
    return httpInterceptor.post(this.ENDPOINTS.UPSERT_SHIPMENTS_AND_ITEMS, body, {
      timeoutMs: UPSERT_SHIPMENTS_TIMEOUT_MS,
    });
  }
}
